/// <amd-module name="Test/common-util/Common" />
import { TestOutcome } from "Test/common-util/Contracts/Test";

export class TestReportColorPalette {
    public static Failed = "#DA0A00";
    public static Passed = "#107C10";
    public static Aborted = "#F15854";
    public static NotExecuted = "#828282";
    public static NotImpacted = "#FFB900";
    public static OtherOutcome = "#BFBFBF";
    public static ExistingFailures = "#F5A9A9";
    public static Duration = "#5DA5DA";
    public static TotalTests = "#B276B2";
    public static AbortedForLegend = "#A80000";
}

/// <summary>
/// Enum for view context
/// </summary>
export enum ViewContext {
    Build = 0,
    Release,
    ExploratorySession,
    WorkItem,
    Unsupported
}

/**
 * @brief The interface is used for inputs to Data providers e.g. Release or Build objects to Release/Build data providers
 */
export interface IData {
    mainData: any; // Main data object: e.g. Build or Release
    subData?: any; // sub-data object: e.g selected environment in a release
    compareWithData?: any; // data object to compare with: e.g. selected build object or selected release object
    payload?: any; // Any additional information that needs to be passed to the Data provider. e.g. count of builds to be fetched for trend chart.
}

export enum ViewContextStatus {
    InProgress,
    Completed,
    Others
}

export class Constants {
    public static buildReportsDataproviderId = "ms.vss-test-web.test-tab-build-summary-data-provider";
    public static buildTestPivotProviderId = "ms.vss-test-web.build-test-results-tab";
    public static releaseTestPivotProviderId = "ms.vss-test-web.release-test-results-tab";
    public static OTHER_OUTCOME_LIST: TestOutcome[] = [
        TestOutcome.Blocked,
        TestOutcome.Error,
        TestOutcome.Inconclusive,
        TestOutcome.InProgress,
        TestOutcome.None,
        TestOutcome.NotApplicable,
        TestOutcome.NotExecuted,
        TestOutcome.Paused,
        TestOutcome.Timeout,
        TestOutcome.Unspecified,
        TestOutcome.Warning
    ];
}

export enum TestResultsGroupPivots {
    Group_By_Container = "AutomatedTestStorage",
    Group_By_Test_Run = "TestRun",
    Group_By_Requirement = "Requirement",
    Group_By_Test_Suite = "TestSuite",
    Group_By_Priority = "Priority",
    Group_By_Owner = "Owner",
    Group_By_None = ""
}

/// <summary>
/// data received from view context source
/// </summary>
export interface IViewContextData {
    viewContext: ViewContext;
    data: IData;
    status?: ViewContextStatus; // Optional Status needed for InProgressData
    contributionSource?: ContributionSource;
    runId?: string;
}

export enum ContributionSource {
    Build = "ms.vss-test-web.build-test-results-tab",
    Release = "ms.vss-test-web.test-result-in-release-environment-editor-tab",
    Pipeline = "ms.vss-test-web.unified-test-results-tab",
    AX = "ms.vss-test-analytics-web.new-test-analytics-hub"
}

export interface IDuration {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}

export interface ITestFailureData {
    newFailures: number;
    existingFailures: number;
    totalFailures: number;
}

export class FeatureFlagConstants {
    public static QueryFromTcmService = "WebAccess.TestManagement.QueryFromTcmService";
    public static TriReportCustomization = "TestManagement.Server.TRIReportCustomization";
    public static InProgressRefreshUsingSignalREnabled: string = "TestManagement.Server.SignalRReportingRuns";
    public static WebAccessTestManagementFiltersForRerun: string = "WebAccess.TestManagement.FiltersForRerun";
    public static PublishOnTcmService: string = "TestManagement.Server.EnablePublishOnTcmService";
    public static TestTabInNewWebPlatform: string = "WebAccess.TestManagement.TestTabInNewWebPlatform";
    public static EnableTestFlakiness: string = "TestManagement.Server.EnableTestFlakiness";
    public static ShowTestProjectSettings: string = "TestManagement.Server.ShowTestProjectSettings";
    public static AssociateToTestCase: string = "WebAccess.TestManagement.AssociateTestPlanToTestCase";
    public static EnableFetchAllResultsUntilMegaBatchSize: string = "TestManagement.Server.FetchAllResultsUntilMegaBatchSize";
    public static EnableFilteringMessage: string = "TestManagement.Server.EnableTestResultsFilteringMessage";
    public static ShowCloudTestLink: string = "WebAccess.TestManagement.ShowCloudTestLink";
    public static CTUrlSharing: string = "WebAccess.TestManagement.CTUrlSharing";
}

export class SourceWorkflow {
    public static BUILD_SOURCE_WORKFLOW = "CI";
    public static RELEASE_SOURCE_WORKFLOW = "CD";
}

export class FilterByFields {
    public static Outcome: string = "Outcome";
    public static Owner: string = "Owner";
    public static Container: string = "AutomatedTestStorage";
    public static TestCaseName: string = "TestCaseName";
    public static TestRun: string = "TestRun";
    public static Branch: string = "Branch";
    public static Tags: string = "Tags";
    public static Stages: string = "Stages";
    public static TestRunId: string = "TestRunId";
}

export class TestResultsOutcomeFilterPivots {
    public static Filter_By_All = "All";
    public static Filter_By_Failed = "Failed";
    public static Filter_By_Passed = "Passed";
    public static Filter_By_Others = "Others";
    public static Filter_By_NotImpacted = "NotImpacted";
    public static Filter_By_PassedOnRerun = "PassedOnRerun";
    public static Filter_By_Aborted = "Aborted";
}

export class ColumnIndices {
    public static Outcome: string = "outcome";
    public static NewFailure: string = "newFailures";
    public static Test: string = "test";
    public static FailingSince: string = "failingSince";
    public static FailingBuild: string = "failingBuild";
    public static Duration: string = "duration";
    public static DateStarted: string = "dateStarted";
    public static DateCompleted: string = "dateCompleted";
    public static Owner: string = "owner";
    public static FailingRelease: string = "failingRelease";
    public static EnvironmentName: string = "environmentName";
    public static Tags: string = "tags";
}

export class IconDetails {
    public iconName: string;
    public className: string;
}

export enum TestOutcomeEnum {
    Unspecified = 0,
    None = 1,
    Passed = 2,
    Failed = 3,
    Inconclusive = 4,
    Timeout = 5,
    Aborted = 6,
    Blocked = 7,
    NotExecuted = 8,
    Warning = 9,
    Error = 10,
    NotApplicable = 11,
    Paused = 12,
    InProgress = 13,
    NotImpacted = 14,
    Others = 15
}

export enum TestResultFlakyState {
    None = 0,
    Marked = 1,
    Unmarked = 2
}

export class TestCaseResultIdentifier {
    public testRunId: number;
    public testResultId: number;

    constructor(testRunId: number, testResultId: number) {
        this.testRunId = testRunId;
        this.testResultId = testResultId;
    }

    public equals(resultIdentifier: TestCaseResultIdentifier) {
        if (!this.testRunId || !this.testResultId || !resultIdentifier) {
            return false;
        }

        return this.testRunId === resultIdentifier.testRunId && this.testResultId === resultIdentifier.testResultId;
    }

    public toString(): string {
        let idBuilder = this.testRunId.toString();

        idBuilder = idBuilder.concat(":");
        idBuilder = idBuilder.concat(this.testResultId.toString());

        return idBuilder.toString();
    }
}

export class TestCaseResultIdentifierWithDuration extends TestCaseResultIdentifier {
    public durationInMs: number;

    constructor(testRunId: number, testResultId: number, durationInMs: number) {
        super(testRunId, testResultId);
        this.durationInMs = durationInMs;
    }
}
